import React, { useMemo } from 'react';
import { SectionLabel } from '@/components/ui/section-label';
import { SimpleInput } from '@/components/form/inputs/SimpleInput';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Layers, Plus, Save } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card/Card';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/shared/EmptyState';
import { SimpleSelect } from '@/components/form/inputs/SimpleSelect';
import { useTranslation } from '@/hooks/useTranslation';
import { useMaterialManager } from '../hooks/useMaterialManager';
import { ArchiveAction } from './ArchiveAction';
import { flattenIsoTree } from '../data/isoMaterialTree';
import type { TraceabilityApiClient } from '../api/TraceabilityApiClient';

type MaterialManagerPanelProps = {
  companyName: string;
  traceabilityApiClient: TraceabilityApiClient | null;
};

const textareaCls =
  'w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none';

export const MaterialManagerPanel: React.FC<MaterialManagerPanelProps> = ({
  companyName,
  traceabilityApiClient,
}) => {
  const { t } = useTranslation();
  const isoOptions = useMemo(() => flattenIsoTree(t), [t]);

  const {
    formMaterial,
    setFormMaterial,
    selectedId,
    formError,
    isLoading,
    isSaving,
    isArchiving,
    archiveConfirm,
    setArchiveConfirm,
    showArchived,
    setShowArchived,
    selectedMaterial,
    isEditing,
    visibleMaterials,
    activeMaterials,
    handleAddOrUpdate,
    handleArchive,
    handleRestore,
    handleSelectMaterial,
    handleNewMaterial,
  } = useMaterialManager(companyName, traceabilityApiClient);

  const isoCategoryLabel = (path: string): string => {
    const found = isoOptions.find((o) => o.value === path);
    return found ? found.label : path;
  };

  return (
    <div className="flex flex-col gap-4">
      {formError && <Callout tone="error">{formError}</Callout>}

      {/* Mobile selector */}
      <div className="mb-4 md:hidden">
        <SimpleSelect
          aria-label={t('traceability.materialManager.list.title')}
          value={selectedId !== undefined ? String(selectedId) : null}
          onChange={(next) => {
            const id = next ? Number(next) : undefined;
            const m = id !== undefined ? activeMaterials.find((x) => x.id === id) : undefined;
            if (m) handleSelectMaterial(m);
            else handleNewMaterial();
          }}
          disabled={isLoading || isSaving || isArchiving}
          noneLabel={t('traceability.materialManager.form.new')}
          options={activeMaterials.map((m) => ({
            value: m.id !== undefined ? String(m.id) : m.name,
            label: m.name,
          }))}
        />
      </div>

      <div className="grid gap-6 md:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
        {/* Form panel */}
        <Card>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <SectionLabel icon={Layers}>
              {t('traceability.materialManager.form.sectionTitle')}
            </SectionLabel>
            {selectedId !== undefined && formMaterial.name && (
              <Badge tone="primary" caps>
                {formMaterial.name}
              </Badge>
            )}
          </div>

          <SimpleInput
            name="material-name"
            label={t('traceability.materialManager.form.fields.name.label')}
            value={formMaterial.name}
            onChange={(val) => setFormMaterial((prev) => ({ ...prev, name: val as string }))}
            placeholder={t('traceability.materialManager.form.fields.name.placeholder')}
            required
          />

          <div className="mt-4">
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">
              {t('traceability.materialManager.form.fields.isoCategory.label')}
            </p>
            <SimpleSelect
              aria-label={t('traceability.materialManager.form.fields.isoCategory.label')}
              value={formMaterial.isoCategory}
              onChange={(next) =>
                setFormMaterial((prev) => ({ ...prev, isoCategory: next ?? '' }))
              }
              noneLabel={t('traceability.materialManager.form.fields.isoCategory.placeholder')}
              options={isoOptions}
            />
          </div>

          <div className="mt-4">
            <SimpleInput
              name="material-designation"
              label={t('traceability.materialManager.form.fields.designation.label')}
              value={formMaterial.designation ?? ''}
              onChange={(val) =>
                setFormMaterial((prev) => ({
                  ...prev,
                  designation: (val as string) || null,
                }))
              }
              placeholder={t('traceability.materialManager.form.fields.designation.placeholder')}
            />
          </div>

          <div className="mt-4">
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">
              {t('traceability.materialManager.form.fields.description.label')}
            </p>
            <textarea
              className={textareaCls}
              rows={3}
              value={formMaterial.description ?? ''}
              onChange={(e) =>
                setFormMaterial((prev) => ({
                  ...prev,
                  description: e.target.value || null,
                }))
              }
              placeholder={t(
                'traceability.materialManager.form.fields.description.placeholder'
              )}
            />
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button
              className="flex-1"
              onClick={handleAddOrUpdate}
              loading={isSaving}
              disabled={isLoading}
            >
              {isSaving ? (
                t('traceability.materialManager.form.submit.saving')
              ) : (
                <>
                  <Save aria-hidden />
                  {isEditing
                    ? t('traceability.materialManager.form.submit.update')
                    : t('traceability.materialManager.form.submit.save')}
                </>
              )}
            </Button>
            <Button
              className="flex-1"
              variant="outline"
              onClick={handleNewMaterial}
              disabled={isSaving || isLoading}
            >
              <Plus aria-hidden />
              {t('traceability.materialManager.form.new')}
            </Button>
          </div>
        </Card>

        {/* List panel — desktop only */}
        <Card padding="sm" className="hidden md:flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-semibold text-foreground">
              {t('traceability.materialManager.list.title')}
            </p>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 cursor-pointer text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-border accent-primary"
                />
                {t('traceability.materialManager.list.showArchived')}
              </label>
              <span className="text-xs text-muted-foreground">
                {t('traceability.materialManager.list.count', {
                  count: activeMaterials.length,
                })}
              </span>
            </div>
          </div>

          <div className="max-h-72 space-y-2 overflow-auto pr-1">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Spinner size="lg" className="text-primary" />
              </div>
            )}
            {!isLoading && visibleMaterials.length === 0 && (
              <EmptyState
                size="compact"
                title={t('traceability.materialManager.list.emptyTitle')}
                description={t('traceability.materialManager.list.emptyHint')}
              />
            )}
            {!isLoading &&
              visibleMaterials.map((material) => (
                <button
                  key={material.id ?? material.name}
                  type="button"
                  onClick={() => handleSelectMaterial(material)}
                  disabled={isSaving || isArchiving}
                  className={cn(
                    'w-full rounded-xl border px-4 py-3 text-left shadow-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50',
                    material.archived
                      ? 'opacity-50 border-border bg-card'
                      : cn(
                          'transition hover:-translate-y-0.5 hover:shadow-md',
                          material.id === selectedId
                            ? 'border-primary/40 bg-primary/5 text-primary'
                            : 'border-border bg-card hover:border-primary/40 hover:bg-primary/5'
                        )
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">{material.name}</p>
                      {material.isoCategory && (
                        <Badge tone="primary" size="sm" className="mt-1">
                          {isoCategoryLabel(material.isoCategory)}
                        </Badge>
                      )}
                      {material.designation && (
                        <p className="mt-0.5 font-mono text-2xs text-muted-foreground truncate">
                          {material.designation}
                        </p>
                      )}
                    </div>
                    {material.archived && (
                      <Badge size="sm" caps>
                        {t('traceability.materialManager.list.archivedBadge')}
                      </Badge>
                    )}
                  </div>
                </button>
              ))}
          </div>

          <ArchiveAction
            selected={selectedMaterial}
            i18nPrefix="traceability.materialManager"
            archiveConfirm={archiveConfirm}
            isArchiving={isArchiving}
            isSaving={isSaving}
            onArchiveClick={() => setArchiveConfirm(true)}
            onArchiveConfirm={handleArchive}
            onArchiveCancel={() => setArchiveConfirm(false)}
            onRestore={handleRestore}
          />
        </Card>
      </div>
    </div>
  );
};
